import type { ReactNode } from 'react';
import { formatMoney } from '@/utils/format';
import type {
  AcquisitionStatus,
  AuditFinding,
  AuditStatus,
  CopyCondition,
  CopyStatus,
  FineStatus,
  LoanStatus,
  MemberStatus,
  ReservationStatus,
} from '@/types/library';

/*
 * Pieces every library screen uses.
 *
 * Kept together for the same reason HR has a shared file: two dozen screens
 * cannot be allowed to grow two dozen slightly different ways of colouring a
 * status. The status maps are exhaustive over their union types on purpose —
 * adding a state on the server becomes a compile error here rather than a blank
 * badge nobody notices.
 */

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

const TONE_CLASS: Record<Tone, string> = {
  neutral: 'border-line bg-surface text-muted',
  success: 'border-[color:var(--sms-success)] text-[color:var(--sms-success)]',
  warning: 'border-[color:var(--sms-warning)] text-[color:var(--sms-warning)]',
  danger: 'border-[color:var(--sms-danger)] text-[color:var(--sms-danger)]',
  info: 'border-accent text-accent',
};

/** A small status pill. Text as well as colour: colour alone is not a label. */
export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.03em] ${TONE_CLASS[tone]}`}
    >
      {children}
    </span>
  );
}

/**
 * A copy's state.
 *
 * MISSING reads as a warning rather than a danger: it means the shelf was
 * searched and the book was not on it, which is a thing to follow up, not a loss
 * anybody has been charged for.
 */
const COPY_TONE: Record<CopyStatus, Tone> = {
  AVAILABLE: 'success',
  ISSUED: 'info',
  RESERVED: 'info',
  LOST: 'danger',
  DAMAGED: 'danger',
  UNDER_REPAIR: 'warning',
  MISSING: 'warning',
  WITHDRAWN: 'neutral',
  ARCHIVED: 'neutral',
};

export function CopyStatusBadge({ status }: { status: CopyStatus }) {
  return <Badge tone={COPY_TONE[status]}>{humanise(status)}</Badge>;
}

const CONDITION_TONE: Record<CopyCondition, Tone> = {
  NEW: 'success',
  GOOD: 'success',
  FAIR: 'warning',
  POOR: 'warning',
  DAMAGED: 'danger',
};

export function ConditionBadge({ condition }: { condition: CopyCondition }) {
  return <Badge tone={CONDITION_TONE[condition]}>{humanise(condition)}</Badge>;
}

const LOAN_TONE: Record<LoanStatus, Tone> = {
  ACTIVE: 'info',
  RETURNED: 'success',
  OVERDUE: 'danger',
  LOST: 'danger',
  CANCELLED: 'neutral',
};

export function LoanStatusBadge({ status, overdue }: { status: LoanStatus; overdue?: boolean }) {
  // An active loan past its date shows as overdue even though the row still
  // says ACTIVE — the desk cares about the fact, not the enum.
  if (status === 'ACTIVE' && overdue) {
    return <Badge tone="danger">overdue</Badge>;
  }
  return <Badge tone={LOAN_TONE[status]}>{humanise(status)}</Badge>;
}

const MEMBER_TONE: Record<MemberStatus, Tone> = {
  ACTIVE: 'success',
  SUSPENDED: 'danger',
  EXPIRED: 'warning',
  CLOSED: 'neutral',
};

export function MemberStatusBadge({ status }: { status: MemberStatus }) {
  return <Badge tone={MEMBER_TONE[status]}>{humanise(status)}</Badge>;
}

const RESERVATION_TONE: Record<ReservationStatus, Tone> = {
  PENDING: 'neutral',
  READY: 'success',
  COLLECTED: 'info',
  EXPIRED: 'warning',
  CANCELLED: 'neutral',
};

export function ReservationStatusBadge({ status }: { status: ReservationStatus }) {
  return <Badge tone={RESERVATION_TONE[status]}>{status === 'READY' ? 'ready' : humanise(status)}</Badge>;
}

const FINE_TONE: Record<FineStatus, Tone> = {
  OUTSTANDING: 'danger',
  PARTIALLY_SETTLED: 'warning',
  PAID: 'success',
  WAIVED: 'info',
  CANCELLED: 'neutral',
};

export function FineStatusBadge({ status }: { status: FineStatus }) {
  return <Badge tone={FINE_TONE[status]}>{humanise(status)}</Badge>;
}

const ACQUISITION_TONE: Record<AcquisitionStatus, Tone> = {
  DRAFT: 'neutral',
  ORDERED: 'info',
  PARTIALLY_RECEIVED: 'warning',
  RECEIVED: 'success',
  CANCELLED: 'neutral',
};

export function AcquisitionStatusBadge({ status }: { status: AcquisitionStatus }) {
  return <Badge tone={ACQUISITION_TONE[status]}>{humanise(status)}</Badge>;
}

const AUDIT_TONE: Record<AuditStatus, Tone> = {
  IN_PROGRESS: 'info',
  COMPLETED: 'success',
  ABANDONED: 'neutral',
};

export function AuditStatusBadge({ status }: { status: AuditStatus }) {
  return <Badge tone={AUDIT_TONE[status]}>{humanise(status)}</Badge>;
}

const FINDING_TONE: Record<AuditFinding, Tone> = {
  PENDING: 'neutral',
  SEEN: 'success',
  MISSING: 'warning',
  MISPLACED: 'warning',
  UNEXPECTED: 'info',
  DAMAGED: 'danger',
};

export function FindingBadge({ finding }: { finding: AuditFinding }) {
  return <Badge tone={FINDING_TONE[finding]}>{humanise(finding)}</Badge>;
}

export function Money({ value, bold = false }: { value?: number | null; bold?: boolean }) {
  return (
    <span
      className={`tabular-nums ${bold ? 'font-bold text-ink' : 'text-ink'}`}
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {value === null || value === undefined ? '—' : formatMoney(value)}
    </span>
  );
}

/** A headline figure with a label under it. */
export function Stat({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
}) {
  const accent =
    tone === 'danger'
      ? 'var(--sms-danger)'
      : tone === 'warning'
        ? 'var(--sms-warning)'
        : tone === 'success'
          ? 'var(--sms-success)'
          : 'var(--sms-accent)';

  return (
    <div className="border border-line bg-bg p-4">
      <div className="h-[3px] w-8" style={{ background: accent }} aria-hidden="true" />
      <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
        {label}
      </p>
      <p className="mt-1 text-[26px] font-extrabold leading-none text-ink">{value}</p>
      {hint !== undefined && <p className="mt-1.5 text-xs text-muted">{hint}</p>}
    </div>
  );
}

/** A labelled row for a detail panel. */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-line py-2 last:border-0">
      <dt className="shrink-0 text-xs uppercase tracking-[0.04em] text-muted">{label}</dt>
      <dd className="min-w-0 text-right text-sm text-ink">{children}</dd>
    </div>
  );
}

/** A horizontal bar for a breakdown, sized against the largest value shown. */
export function BarRow({
  label,
  value,
  max,
  suffix,
}: {
  label: string;
  value: number;
  max: number;
  suffix?: ReactNode;
}) {
  const width = max <= 0 ? 0 : Math.max(2, Math.round((value / max) * 100));
  return (
    <div className="py-1.5">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="min-w-0 truncate text-ink">{label}</span>
        <span className="shrink-0 tabular-nums text-muted">{suffix ?? value}</span>
      </div>
      <div className="mt-1 h-1.5 w-full bg-surface">
        <div className="h-full bg-accent" style={{ width: `${width}%` }} aria-hidden="true" />
      </div>
    </div>
  );
}

/**
 * The reasons the server gave for refusing something.
 *
 * Rendered verbatim. §38 says a critical rule must not live only in React, and
 * the corollary is that the explanation must not either — restating the server's
 * decision in our own words is how the two come to disagree.
 */
export function Blockers({ reasons, title }: { reasons: string[]; title?: string }) {
  if (reasons.length === 0) {
    return null;
  }
  return (
    <div className="border border-[color:var(--sms-danger)] bg-surface p-3">
      {title !== undefined && (
        <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[color:var(--sms-danger)]">
          {title}
        </p>
      )}
      <ul className="mt-1 space-y-0.5 text-sm text-ink">
        {reasons.map((reason) => (
          <li key={reason}>{reason}</li>
        ))}
      </ul>
    </div>
  );
}

/** UNDER_REPAIR → "under repair". */
export function humanise(value: string): string {
  return value.replaceAll('_', ' ').toLowerCase();
}

/** A title's authors as one line, for a table cell. */
export function authorsOf(authors: { name: string }[] | undefined): string {
  return authors === undefined || authors.length === 0
    ? '—'
    : authors.map((author) => author.name).join(', ');
}

export function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** A date that many days ago, for a report's default range. */
export function daysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
}

/** "3 days overdue" / "due today" — the phrasing a desk actually reads. */
export function overdueText(days: number): string {
  if (days <= 0) {
    return 'not overdue';
  }
  return `${days} day${days === 1 ? '' : 's'} overdue`;
}
